import { CustomAlert, FormEntry } from "components";
import { useState, Fragment } from "react";
import apiService from "services/api";
import { useHistory, Link, useParams } from "react-router-dom";
import { useNotificationContext } from "contexts";
import { useForm, useFieldArray } from "react-hook-form";
import { Button, Collapse } from "reactstrap";
import { NestedFieldArray, HorizontalLine } from "./components";
import { useQuery } from "react-query";
import { courseSchema, newLessonSchema, mapLessonsByCategory } from "./constants";
import { capitalize } from "utils/capitalize";
import { FiX, FiChevronsDown, FiChevronsUp } from "react-icons/fi";

const EditCoursePage = () => {
	const history = useHistory();
	const { course_id } = useParams();
	const {
		register,
		handleSubmit,
		reset,
		control,
		watch,
		formState: { errors },
	} = useForm({
		defaultValues: courseSchema,
	});

	const {
		fields: lessons,
		append,
		remove,
	} = useFieldArray({
		control,
		name: "lessons",
	});

	const { createNotification } = useNotificationContext();
	const [defaultCourse, setDefaultCourse] = useState({});

	useQuery(["course", course_id], () => apiService.course.get(course_id), {
		onSuccess: (course) => {
			const tempData = {
				...course,
				lessons: course?.lessons.map((lesson, index) => ({
					...lesson,
					index,
				})),
			};

			reset(tempData);
			setDefaultCourse(tempData);
		},
	});

	const submitNewCourse = (data) => {
		apiService.course
			.update(course_id, data)
			.then((response) => {
				createNotification({
					type: "success",
					message: "Successfully edited course",
				});
				history.push("/course");
			})
			.catch((error) =>
				createNotification({
					type: "danger",
					message: error.message,
				})
			);
	};

	const addAnother = (category) => append(newLessonSchema(category));

	const removeLesson = (idx) => remove(idx);

	const resetFields = () => {
		reset(defaultCourse);
	};

	const [openCategories, setOpenCategories] = useState([]);

	const lessonsByCategories = mapLessonsByCategory(lessons);

	const toggleCollapse = (category) => {
		if (openCategories.includes(category)) return setOpenCategories((categories) => categories.filter((cat) => cat !== category));
		return setOpenCategories((categories) => [...categories, category]);
	};

	const initialDeleteLessonModalState = {
		show: false,
		id: null,
	};

	const [deleteLessonModalProps, setDeleteLessonModalProps] = useState(initialDeleteLessonModalState);

	const hideDeleteModal = () => setDeleteLessonModalProps(initialDeleteLessonModalState);

	const openDeleteModal = (id) => setDeleteLessonModalProps({ show: true, id });

	return (
		<form onSubmit={handleSubmit(submitNewCourse)} className="content">
			<div className="text-center mb-5 font-weight-bold display-5 text-white">Edit Course</div>
			<CustomAlert
				show={deleteLessonModalProps.show}
				hideModal={hideDeleteModal}
				title="Confirmation"
				description="Delete lesson?"
				submitButtonText="Confirm"
				submitModal={() => {
					removeLesson(deleteLessonModalProps.id);
					hideDeleteModal();
				}}
			/>

			<div className="d-flex justify-content-center">
				<div className="w-100">
					<div className="mb-5">
						<Link to={`/course/view/${course_id}`}>
							<button className="btn btn-sm btn-info">&lt; Back</button>
						</Link>
					</div>

					<FormEntry formLabel="Name" placeholder="Course Name" name="name" type="text" required={true} register={register} />

					<FormEntry formLabel="Price" placeholder="Course Price" name="price" type="number" required={true} register={register} />

					<div className="text-center my-5 font-weight-bold display-5 text-white">Lessons</div>
					{lessonsByCategories.map(({ lessons, category }) => (
						<div
							key={category}
							className="col-12 py-2 mb-5"
							style={{
								border: "2px solid #ffffff25",
								borderRadius: 8,
								backgroundColor: "#1D253B",
								cursor: "pointer",
							}}
							id="accordion"
							role="tablist"
						>
							<div
								className="d-flex justify-content-between align-items-center mb-2"
								aria-expanded={openCategories.includes(category)}
								data-parent="#accordion"
								data-toggle="collapse"
								onClick={() => {
									if (lessons.length) toggleCollapse(category);
								}}
							>
								<div className="display-6 font-weight-bold text-white">{capitalize(category)}</div>
								<Button
									className="btn-link edit text-warning d-flex align-items-center border border-warning"
									color="primary"
									size="sm"
									onClick={(e) => {
										if (!openCategories.includes(category)) toggleCollapse(category);
										addAnother(category);
										e.stopPropagation();
									}}
									type="button"
								>
									+ Lesson
								</Button>
							</div>
							<HorizontalLine />
							{lessons.length ? (
								lessons.map((lesson, i) => (
									<div key={lesson.id} className="my-1">
										<Collapse isOpen={openCategories.includes(category)} onClick={(e) => e.stopPropagation()}>
											<div className="d-flex justify-content-between align-items-baseline my-2">
												<div className="text-white font-weight-bold ml-2">Lesson {i + 1}</div>

												<Button
													color="warning"
													size="sm"
													className="btn-link edit text-danger d-flex align-items-center border border-danger"
													onClick={() => openDeleteModal(lesson.index)}
													type="button"
												>
													<FiX className="mr-1" /> Lesson
												</Button>
											</div>
											<FormEntry
												type="text"
												formLabel="Name"
												name={`lessons.[${lesson.index}].name`}
												placeholder={`Lesson ${i + 1} Name`}
												required={true}
												register={register}
												error={errors.lessons?.[lesson.index]?.name}
											/>

											<FormEntry
												type="text"
												formLabel="Code"
												name={`lessons.[${lesson.index}].code`}
												placeholder={`Lesson ${i + 1} Code`}
												required={true}
												register={register}
												error={errors.lessons?.[lesson.index]?.code}
											/>

											<FormEntry
												type="text"
												formLabel="Filepath"
												name={`lessons.[${lesson.index}].filepath`}
												placeholder={`Lesson ${i + 1} File path`}
												required={true}
												register={register}
												error={errors.lessons?.[lesson.index]?.filepath}
											/>

											<FormEntry
												type="text"
												formLabel="Classwork URL"
												name={`lessons.[${lesson.index}].classworkURL`}
												placeholder={`Lesson ${i + 1} Classwork URL`}
												register={register}
												error={errors.lessons?.[lesson.index]?.classworkURL}
											/>

											<FormEntry
												type="text"
												formLabel="Test & Review URL"
												name={`lessons.[${lesson.index}].testAndReviewURL`}
												placeholder={`Lesson ${i + 1} Test & Review URL`}
												register={register}
												error={errors.lessons?.[lesson.index]?.testAndReviewURL}
											/>

											<FormEntry
												type="text"
												formLabel="Homework URL"
												name={`lessons.[${lesson.index}].homeworkURL`}
												placeholder={`Lesson ${i + 1} Homework URL`}
												register={register}
												error={errors.lessons?.[lesson.index]?.homeworkURL}
											/>

											<NestedFieldArray
												control={control}
												register={register}
												index={i}
												errors={errors}
												namePrefix={`lessons.${lesson.index}.learningObjectives`}
											/>
											{i !== lessons.length - 1 && <HorizontalLine />}
										</Collapse>
									</div>
								))
							) : (
								<div className="text-center text-muted mt-3 mb-1">No Lessons Yet</div>
							)}
							{Boolean(lessons.length) && (
								<div
									className="text-center text-muted mt-3 mb-1"
									onClick={() => {
										if (lessons.length) toggleCollapse(category);
									}}
								>
									{!openCategories.includes(category) ? (
										<FiChevronsDown style={{ fontSize: 28 }} />
									) : (
										<FiChevronsUp style={{ fontSize: 28 }} />
									)}
								</div>
							)}
						</div>
					))}

					<div className="d-flex justify-content-end mt-5">
						<button className="btn btn-success mx-1" type="submit">
							Save
						</button>
						<button className="btn btn-secondary" type="button" onClick={resetFields}>
							Reset
						</button>
					</div>
				</div>
			</div>
		</form>
	);
};

export default EditCoursePage;
